import React, { useId } from "react";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import { useTranslation } from "lib/i18n/use-translation";
import { Button } from "components/ui";
import {
  savedProgressRemaining,
  SavedProgressRemaining,
} from "utility/savedProgress";

import styles from "./save-progress-modal.module.scss";

// "ask" offers to save on the way out. "saved" is for answers that are
// already being saved as they are given: it confirms the save instead of
// asking, showing a pending state until the save is confirmed.
type SaveProgressModalProps =
  | {
      variant?: "ask";
      onKeepGoing: () => void;
      onSaveAndExit: () => void;
      expiresAt?: string | null;
      isSaving?: boolean;
      hasSaveError?: boolean;
    }
  | {
      variant: "saved";
      onDone: () => void;
      onKeepGoing: () => void;
      expiresAt?: string | null;
      isSaving: boolean;
    };

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Built from pluralised fragments rather than one sentence per shape, so a
// locale controls both its plural forms and how the two halves join.
function formatRemaining(t: Translate, remaining: SavedProgressRemaining) {
  switch (remaining.unit) {
    case "days":
      return t("assessment:saveProgress.durationDays", {
        count: remaining.days,
      });
    case "hours":
      return t("assessment:saveProgress.durationHours", {
        count: remaining.hours,
      });
    case "minutes":
      return t("assessment:saveProgress.durationMinutes", {
        count: remaining.minutes,
      });
    case "hoursMinutes":
      return t("assessment:saveProgress.durationHoursAndMinutes", {
        hours: t("assessment:saveProgress.durationHours", {
          count: remaining.hours,
        }),
        minutes: t("assessment:saveProgress.durationMinutes", {
          count: remaining.minutes,
        }),
      });
  }
}

function SaveProgressModal(props: SaveProgressModalProps) {
  const { t } = useTranslation(["assessment"]);
  const titleId = useId();
  const duration = formatRemaining(t, savedProgressRemaining(props.expiresAt));

  if (props.variant === "saved") {
    return (
      <Modal
        isOpen={true}
        style={{ width: "auto", maxWidth: "400px" }}
        className={styles["modal"]}
        contentClassName={styles["modal-content"]}
        labelledBy={titleId}
        data-testid="save-progress-modal"
      >
        <ModalBody className={styles["body"]}>
          {/* Stays mounted across the swap so the confirmation is announced. */}
          <div role="status">
            <h1 id={titleId}>
              {props.isSaving
                ? t("assessment:saveProgress.saving")
                : t("assessment:saveProgress.savedTitle")}
            </h1>
            {!props.isSaving && (
              <p>{t("assessment:saveProgress.savedBody", { duration })}</p>
            )}
          </div>
        </ModalBody>
        <ModalFooter className={styles["footer"]}>
          <Button
            variant="enclosed-primary"
            onClick={props.onDone}
            disabled={props.isSaving}
            loading={props.isSaving}
          >
            {t("assessment:saveProgress.done")}
          </Button>
          {props.isSaving && (
            <Button variant="ghost" onClick={props.onKeepGoing}>
              {t("assessment:saveProgress.keepGoing")}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    );
  }

  const {
    onKeepGoing,
    onSaveAndExit,
    isSaving = false,
    hasSaveError = false,
  } = props;

  return (
    <Modal
      isOpen={true}
      style={{ width: "auto", maxWidth: "400px" }}
      className={styles["modal"]}
      contentClassName={styles["modal-content"]}
      data-testid="save-progress-modal"
    >
      <ModalBody className={styles["body"]}>
        <h1>{t("assessment:saveProgress.title")}</h1>
        <p>{t("assessment:saveProgress.body", { duration })}</p>
        {hasSaveError && (
          <p className={styles["error"]} role="alert">
            {t("assessment:saveProgress.error")}
          </p>
        )}
      </ModalBody>
      <ModalFooter className={styles["footer"]}>
        <Button
          variant="enclosed-primary"
          onClick={onKeepGoing}
          disabled={isSaving}
        >
          {t("assessment:saveProgress.keepGoing")}
        </Button>
        <Button variant="ghost" onClick={onSaveAndExit} disabled={isSaving}>
          {t("assessment:saveProgress.saveAndExit")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SaveProgressModal;
